// src/components/OrdersTable.jsx
import React, { useContext, useEffect, useState } from "react";
import { TimezoneContext } from "../../../../../context/TimezoneContext";
import { formatUtcToTz } from "../../../../../utils/datetime";

/* --------------------------------------------------------------
   Config & helpers
---------------------------------------------------------------- */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";
function getAuthHeaders() {
  const token = localStorage.getItem("access_token") || "";
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

export default function OrdersTable({
  selectedRestaurant = "all",
  startDate = null,
  endDate   = null,
  isMobile  = false,
  pageSize  = 15,
}) {
  const { timezone } = useContext(TimezoneContext);

  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error,   setError]         = useState(null);
  const [page,    setPage]          = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /* ------------------------------------------------------------
     FETCH (dipende anche da 'page')
  ------------------------------------------------------------ */
  useEffect(() => {
    let cancel = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const qs = [
          `page=${page}`,
          `page_size=${pageSize}`,
        ];
        if (selectedRestaurant && selectedRestaurant !== "all") {
          qs.push(`restaurant_id=${selectedRestaurant}`);
        }
        if (startDate) qs.push(`start_date=${startDate}`);
        if (endDate)   qs.push(`end_date=${endDate}`);

        let url = `${API_BASE_URL}/orders/session_summary?${qs.join("&")}`;

        const res = await fetch(url, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error(res.status);

        const data = await res.json();
        if (!cancel) {
          setRows(Array.isArray(data.items) ? data.items : []);
          setTotalPages(data.total_pages || 1);
        }
      } catch (err) {
        if (!cancel) {
          console.error(err);
          setError(`Errore caricamento: ${err.message}`);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();

    return () => { cancel = true; };
  }, [selectedRestaurant, startDate, endDate, page, pageSize]);

  /* ------------------------------------------------------------
     Gestione paginazione
  ------------------------------------------------------------ */
  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  if (loading) return <div style={st.noData}>Caricamento…</div>;
  if (error)   return <div style={st.error}>{error}</div>;

  if (!rows.length) {
    return (
      <div>
        <div style={st.noData}>Nessun ordine trovato.</div>
        <PaginationControls {...{ page, totalPages, goPrev, goNext }} />
      </div>
    );
  }

  /* --------------------------------------------------------------
     RENDER DESKTOP
  -------------------------------------------------------------- */
  if (!isMobile) {
    return (
      <>
        <table style={st.table}>
          <thead>
            <tr>
              <th style={st.th}>Data Invio</th>
              <th style={st.th}>Tavolo</th>
              <th style={st.th}>Sessione</th>
              <th style={st.th}>Piatti</th>
              <th style={st.th}>Totale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const dateSent = row.sent_at
                ? formatUtcToTz(row.sent_at, timezone)
                : "-";

              return (
                <tr key={(row.session_id || "nosession") + "_" + i} style={st.tr}>
                  <td style={st.td}>{dateSent}</td>
                  <td style={st.td}>{row.table_number || "-"}</td>
                  <td style={st.td}>{row.session_id || "-"}</td>
                  <td style={st.td}>
                    {row.items.map((it) => (
                      <div key={it.menu_item_id}>
                        {it.name} × {it.quantity} — €
                        {(it.price * it.quantity).toFixed(2)}
                      </div>
                    ))}
                  </td>
                  <td style={st.td}>
                    <strong>€ {row.total.toFixed(2)}</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <PaginationControls {...{ page, totalPages, goPrev, goNext }} />
      </>
    );
  }

  /* --------------------------------------------------------------
     RENDER MOBILE
  -------------------------------------------------------------- */
  return (
    <>
      {rows.map((row, i) => {
        const dateSent = row.sent_at
          ? formatUtcToTz(row.sent_at, timezone)
          : "-";

        return (
          <div key={(row.session_id || "nosession") + "_" + i} style={st.card}>
            <div style={st.cardHeader}>
              <strong>Tavolo {row.table_number || "-"}</strong>
              <span>{dateSent}</span>
            </div>
            <div style={st.cardContent}>
              <p><strong>Sessione:</strong> {row.session_id || "-"}</p>
              <hr />
              {row.items.map((it) => (
                <div key={it.menu_item_id}>
                  {it.name} × {it.quantity} — €
                  {(it.price * it.quantity).toFixed(2)}
                </div>
              ))}
              <hr />
              <p>
                <strong>Totale: € {row.total.toFixed(2)}</strong>
              </p>
            </div>
          </div>
        );
      })}

      <PaginationControls {...{ page, totalPages, goPrev, goNext }} />
    </>
  );
}

/* --------------------------------------------------------------
   PaginationControls – componente interno
---------------------------------------------------------------- */
function PaginationControls({ page, totalPages, goPrev, goNext }) {
  return (
    <div style={st.pagination}>
      <button onClick={goPrev} disabled={page === 1}>
        ‹
      </button>
      <span>{page} / {totalPages}</span>
      <button onClick={goNext} disabled={page === totalPages}>
        ›
      </button>
    </div>
  );
}

/* --------------------------------------------------------------
   STYLES
---------------------------------------------------------------- */
const st = {
  noData: {
    padding: "1rem",
    textAlign: "center",
    color: "#333",
  },
  error: {
    padding: "1rem",
    textAlign: "center",
    color: "red",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "0.75rem",
    borderBottom: "2px solid #336699",
    backgroundColor: "#336699",
    color: "#fff",
  },
  tr: { borderBottom: "1px solid #ccc" },
  td: { padding: "0.75rem", verticalAlign: "top" },
  card: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginBottom: "1rem",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    padding: "1rem",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
    color: "#FF6600",
  },
  cardContent: { color: "#333" },
  pagination: {
    marginTop: "1rem",
    display: "flex",
    justifyContent: "center",
    gap: "0.5rem",
  },
};
